/**
 * Shared vocabulary for the walkthrough: the step sequence, the device probes
 * that decide whether the optional-tools step exists, and the NPI rule.
 *
 * Extracted from IntroToMedsender so the step modules under ./steps can name a
 * step without importing the container that renders them, which would be a
 * cycle. The container keeps loading, navigation and persistence; everything
 * here is pure.
 */

export const STEP_KEYS = {
  welcome: 'Welcome',
  workspace: 'Workspace setup',
  notifications: 'Notifications',
  security: 'Account security',
  tools: 'Add Medsender to your workflow',
  provider: 'Provider details',
  start: 'Get started',
};

// Analytics subject for a step. Every walkthrough event that names a step —
// forward, back, and the save-then-advance paths — goes through this, so the
// events compose into one funnel instead of drifting apart per call site.
export const stepEventName = (step) => (step || '').toLowerCase().replace(/ /g, '_');

export const notificationKeys = [
  'notification_fax_email',
  'email_notification_emailed_records',
  'email_notification_assigned_records',
  'email_notification_emr_upload_failures',
  'failed_sent_faxes_notification',
];

// Mirrors the User model's `validates :npi, format: { with: /\A\d{10}\z/ }`
// (allow_blank, plus presence when is_clinical_provider). Kept as the single
// source of truth for the client so the UI can never submit something the API
// will reject.
const NPI_PATTERN = /^\d{10}$/;
export const isValidNpi = (npi) => NPI_PATTERN.test(npi || '');
export const NPI_INPUT_ID = 'walkthrough-npi';
export const NPI_ERROR_ID = 'walkthrough-npi-error';

export const chromeExtensionLink = 'https://chrome.google.com/webstore/detail/medsender/onpcajmldffklnfccjolcccoijaogggl';

const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];

// Both choices land on `/app`. The guided tutorial (PR #4490, "Learn Medsender")
// is an app-wide overlay mounted by containers/App, not a route, so there is no
// URL to send the user to: what distinguishes the two choices is whether the
// checklist opens itself on arrival. That is decided by
// `shouldAutoOpenTutorialMenu`, which reads the `walkthrough_start_destination`
// the final step saves.
export const completionChoices = [
  { key: 'home', label: 'Home', path: '/app' },
  { key: 'tutorial', label: 'Guided tutorial', path: '/app' },
];

export const isSupportedWindowsDevice = (navigatorObject = window.navigator) => (
  windowsPlatforms.indexOf(navigatorObject.platform) !== -1
  && navigatorObject.userAgent.indexOf('Windows NT 5.1') === -1
);

export const getDeviceTools = ({ chrome, windows }) => ({
  chromeExtension: chrome,
  printerDriver: windows,
});

export const getWalkthroughSteps = (device = {}) => {
  const tools = getDeviceTools(device);
  const steps = [
    STEP_KEYS.welcome,
    STEP_KEYS.workspace,
    STEP_KEYS.notifications,
    STEP_KEYS.security,
  ];
  if (tools.chromeExtension || tools.printerDriver) steps.push(STEP_KEYS.tools);
  return [...steps, STEP_KEYS.provider, STEP_KEYS.start];
};

// A fax number arrives either as the serialized tuple the API sends
// (`[number, ownerName, ownerId, ownerType, id]`) or as an object; both shapes
// are in play depending on which endpoint hydrated the list.
export const faxNumberValue = (faxNumber) => (Array.isArray(faxNumber) ? faxNumber[0] : faxNumber?.number);
export const faxNumberOwner = (faxNumber) => (Array.isArray(faxNumber) ? faxNumber[1] : faxNumber?.organization);
